import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { api } from "@/lib/api";

export default function PlatformTopBarSearch() {
    const navigate = useNavigate();
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [tenants, setTenants] = useState([]);
    const wrapRef = useRef(null);

    useEffect(() => {
        const q = query.trim();
        if (q.length < 2) {
            setTenants([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        const timer = setTimeout(async () => {
            try {
                const r = await api.get("/platform/tenants", { params: { q } });
                setTenants((r.data || []).slice(0, 8));
            } catch {
                setTenants([]);
            } finally {
                setLoading(false);
            }
        }, 280);

        return () => clearTimeout(timer);
    }, [query]);

    const pick = (t) => {
        setOpen(false);
        setQuery("");
        navigate(`/platform/salons/${t.id}`);
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverAnchor asChild>
                <div ref={wrapRef} className="relative w-full">
                    <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[#8A8178]" />
                    <Input
                        value={query}
                        onChange={(e) => {
                            setQuery(e.target.value);
                            setOpen(true);
                        }}
                        onFocus={() => setOpen(true)}
                        placeholder="Search salons…"
                        className="h-9 border-[#E8E4DE] bg-[#FFFEF9] pl-9 text-sm font-medium text-[#2D2926] shadow-sm placeholder:text-[#9A9088] focus-visible:border-[#D4AF37]/55"
                        data-testid="platform-topbar-search"
                    />
                </div>
            </PopoverAnchor>
            <PopoverContent
                align="center"
                className="w-[var(--radix-popover-trigger-width)] rounded-xl border-[#E8E4DE] p-0 font-['Outfit',system-ui,sans-serif] shadow-[0_12px_40px_rgba(45,41,38,0.12)]"
                onOpenAutoFocus={(e) => e.preventDefault()}
            >
                {loading && (
                    <p className="px-4 py-3 text-sm font-medium text-[#8A8178]">Searching…</p>
                )}
                {!loading && query.trim().length < 2 && (
                    <p className="px-4 py-3 text-sm font-medium text-[#8A8178]">Type 2+ characters</p>
                )}
                {!loading && query.trim().length >= 2 && tenants.length === 0 && (
                    <p className="px-4 py-3 text-sm font-medium text-[#8A8178]">No salons found</p>
                )}
                {!loading && tenants.length > 0 && (
                    <ul className="max-h-64 overflow-y-auto py-1">
                        {tenants.map((t) => (
                            <li key={t.id}>
                                <button
                                    type="button"
                                    onClick={() => pick(t)}
                                    className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-[#FAF9F6]"
                                >
                                    <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#FDF3DC]">
                                        <Building2 className="size-4 text-[#C9A227]" />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="truncate text-sm font-semibold text-[#2D2926]">{t.name}</p>
                                        <p className="truncate text-xs text-[#8A8178]">
                                            {[t.city, t.slug].filter(Boolean).join(" · ")}
                                        </p>
                                    </div>
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </PopoverContent>
        </Popover>
    );
}
